import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Plus, RefreshCw, Eye, Paperclip, Upload, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { AuditTimeline } from './ExpensesPage';
import { DateInput } from '@/components/ui/date-input';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const fmtMoney = (v, currency = 'AED') => {
  const n = Number(v || 0);
  return `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10) : '—');
const STATUS_TONE = {
  draft: 'bg-slate-500/20 text-slate-300',
  submitted: 'bg-amber-500/20 text-amber-300',
  pending_approval: 'bg-amber-500/20 text-amber-300',
  approved: 'bg-emerald-500/20 text-emerald-300',
  rejected: 'bg-rose-500/20 text-rose-300',
  pending_reimbursement: 'bg-blue-500/20 text-blue-300',
  settled: 'bg-emerald-600/30 text-emerald-200',
  cancelled: 'bg-zinc-500/20 text-zinc-400',
};
const StatusPill = ({ status }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded text-xs ${STATUS_TONE[status] || 'bg-slate-500/20 text-slate-300'}`}
    data-testid={`my-expense-status-${status}`}
  >
    {(status || '—').replace(/_/g, ' ')}
  </span>
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export default function MyExpensesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);
  const [mcSettings, setMcSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/my`, { headers: authHeaders() });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load your expenses');
    } finally { setLoading(false); }
  }, []);

  const loadCats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/expenses/categories`, { headers: authHeaders() });
      setCategories(r.data.categories || []);
    } catch (_e) { /* noop */ }
  }, []);

  useEffect(() => { load(); loadCats(); }, [load, loadCats]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/manager-cash/settings`, { headers: authHeaders() })
      .then(r => setMcSettings(r.data))
      .catch(() => setMcSettings(null));
  }, []);

  const pendingItems = items.filter(i => ['draft', 'submitted', 'pending_approval'].includes(i.status));
  const approvedItems = items.filter(i => ['approved', 'pending_reimbursement'].includes(i.status));
  const reimbursedItems = items.filter(i => i.status === 'settled');
  const sum = (arr) => arr.reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="my-expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-wt-text-primary flex items-center gap-2">
            <Receipt className="w-6 h-6 text-wt-gold" />
            My Expenses
          </h1>
          <p className="text-wt-text-secondary text-sm">
            Submit out-of-pocket expenses for reimbursement and track approvals
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="my-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setShowForm(true)} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="my-new-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> New Expense Request
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Pending / In Review" value={fmtMoney(sum(pendingItems))} count={pendingItems.length} tone="text-amber-300" testid="my-total-pending" />
        <SummaryCard label="Approved" value={fmtMoney(sum(approvedItems))} count={approvedItems.length} tone="text-emerald-300" testid="my-total-approved" />
        <SummaryCard label="Reimbursed / Settled" value={fmtMoney(sum(reimbursedItems))} count={reimbursedItems.length} tone="text-blue-300" testid="my-total-reimbursed" />
        <SummaryCard label="Total Filed" value={items.length.toString()} count={null} tone="text-wt-text-primary" testid="my-total-filed" />
      </div>

      <div className="bg-wt-paper border border-wt-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="my-expenses-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-wt-text-muted border-b border-wt-border">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Reviewer</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-wt-text-muted">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-wt-text-muted">No expenses yet. Click <b>New Expense Request</b> to submit one.</td></tr>
            ) : items.map(e => (
              <tr key={e.id} className="border-b border-wt-border last:border-0 hover:bg-wt-bg/40" data-testid={`my-expense-row-${e.id}`}>
                <td className="px-4 py-3 text-wt-text-secondary">{fmtDate(e.expense_date)}</td>
                <td className="px-4 py-3 text-wt-text-primary">
                  <button onClick={() => setDetail(e)} className="hover:text-wt-gold text-left">
                    {e.title}
                  </button>
                  {(e.attachments || []).length > 0 && (
                    <Paperclip className="inline w-3 h-3 ml-1 text-wt-text-muted" />
                  )}
                  {e.payment_source === 'manager_cash' && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-wt-gold/15 text-wt-gold" data-testid={`my-expense-source-${e.id}`}>manager cash</span>
                  )}
                </td>
                <td className="px-4 py-3 text-wt-text-secondary">{e.category_name || '—'}</td>
                <td className="px-4 py-3 text-right font-mono text-wt-text-primary">{fmtMoney(e.amount)}</td>
                <td className="px-4 py-3"><StatusPill status={e.status} /></td>
                <td className="px-4 py-3 text-wt-text-secondary">{e.approved_by_name || (e.status === 'rejected' ? 'Rejected' : '—')}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="icon" variant="ghost" onClick={() => setDetail(e)} data-testid={`my-view-expense-${e.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <MyExpenseFormModal
          categories={categories}
          mcSettings={mcSettings}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {detail && (
        <MyExpenseDetailModal expense={detail} onClose={() => setDetail(null)} onChanged={load} />
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, count, tone, testid }) => (
  <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid={testid}>
    <div className="text-wt-text-muted text-xs uppercase tracking-wide">{label}</div>
    <div className={`mt-2 text-xl font-semibold ${tone}`}>{value}</div>
    {count !== null && <div className="mt-1 text-xs text-wt-text-muted">{count} item{count === 1 ? '' : 's'}</div>}
  </div>
);

const MyExpenseFormModal = ({ categories, mcSettings, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: '', category_id: '', amount: '',
    expense_date: new Date().toISOString().slice(0, 10),
    description: '', vendor_name: '', invoice_number: '',
  });
  const [paidFrom, setPaidFrom] = useState('own');
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  const needsReceipt = paidFrom === 'manager_cash' && mcSettings
    && Number(form.amount || 0) >= mcSettings.receipt_required_above && !file;

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.category_id) { toast.error('Category is required'); return; }
    if (!Number(form.amount) || Number(form.amount) <= 0) { toast.error('Amount must be > 0'); return; }
    setSaving(true);
    try {
      if (paidFrom === 'manager_cash') {
        let attachments = [];
        if (file) {
          const fd = new FormData();
          fd.append('file', file);
          const up = await axios.post(`${API_BASE_URL}/manager-cash/attachments`, fd, { headers: authHeaders() });
          attachments = [up.data];
        }
        await axios.post(`${API_BASE_URL}/manager-cash/expenses`,
          { ...form, amount: Number(form.amount), attachments },
          { headers: authHeaders() });
        toast.success('Expense created from manager cash — pending approval');
        onSaved();
        return;
      }
      const payload = { ...form, amount: Number(form.amount), paid_by: 'employee' };
      const r = await axios.post(`${API_BASE_URL}/expenses`, payload, { headers: authHeaders() });
      const expId = r.data.id;
      // Optional receipt upload
      if (file && expId) {
        const fd = new FormData();
        fd.append('file', file);
        try {
          await axios.post(`${API_BASE_URL}/expenses/${expId}/attachments`, fd, {
            headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
          });
        } catch (uerr) {
          toast.warning('Expense submitted, but receipt upload failed: ' + (uerr.response?.data?.detail || uerr.message));
        }
      }
      toast.success('Expense submitted for approval');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="my-expense-form-modal">
        <DialogHeader><DialogTitle>New Expense Request</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {mcSettings && (
            <div className="sm:col-span-2">
              <Label>Paid From *</Label>
              <Select value={paidFrom} onValueChange={setPaidFrom}>
                <SelectTrigger data-testid="my-form-paid-from"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="own">My own money (reimbursement)</SelectItem>
                  <SelectItem value="manager_cash">Manager cash wallet</SelectItem>
                </SelectContent>
              </Select>
              {paidFrom === 'manager_cash' && (
                <p className="text-[11px] text-wt-text-muted mt-1">Deducted from your cash wallet after approval — also appears in Expense Management.</p>
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="my-form-title" />
          </div>
          <div>
            <Label>Category *</Label>
            <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
              <SelectTrigger data-testid="my-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="my-form-amount" />
          </div>
          <div>
            <Label>Expense Date *</Label>
            <DateInput  value={form.expense_date} onChange={e => setForm({ ...form, expense_date: e.target.value })} data-testid="my-form-date" />
          </div>
          <div>
            <Label>Vendor (optional)</Label>
            <Input value={form.vendor_name} onChange={e => setForm({ ...form, vendor_name: e.target.value })} data-testid="my-form-vendor" />
          </div>
          <div className="sm:col-span-2">
            <Label>Invoice Number (optional)</Label>
            <Input value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} data-testid="my-form-invoice" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description / Notes</Label>
            <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} data-testid="my-form-description" />
          </div>
          <div className="sm:col-span-2">
            <Label>Receipt (JPG/PNG/WEBP/PDF, max 5 MB)</Label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-wt-text-secondary file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-wt-gold/20 file:text-wt-gold file:cursor-pointer"
              data-testid="my-form-receipt"
            />
            {file && <div className="text-xs text-wt-text-muted mt-1 flex items-center gap-2">{file.name} · {(file.size / 1024).toFixed(1)} KB
              <button type="button" onClick={() => setFile(null)} className="text-rose-400 hover:text-rose-300" data-testid="my-form-remove-receipt"><X className="w-3 h-3" /></button>
            </div>}
            {needsReceipt && (
              <p className="text-[11px] text-amber-400 mt-1">Receipt upload is required for amounts of {mcSettings.receipt_required_above} and above (manager cash)</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-wt-gold hover:bg-wt-gold/90 text-black" data-testid="my-form-save-btn">
            {saving ? 'Submitting…' : 'Submit for Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MyExpenseDetailModal = ({ expense, onClose, onChanged }) => {
  const [exp, setExp] = useState(expense);
  const [uploading, setUploading] = useState(false);
  const [audit, setAudit] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API_BASE_URL}/expenses/${expense.id}`, { headers: authHeaders() }),
        axios.get(`${API_BASE_URL}/expenses/${expense.id}/audit`, { headers: authHeaders() }),
      ]);
      setExp(r.data);
      setAudit(a.data.entries || []);
    } catch (_e) { /* noop */ }
  }, [expense.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_BASE_URL}/expenses/${expense.id}/attachments`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Receipt uploaded');
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/expenses/${expense.id}/attachments/${att.id}`, { headers: authHeaders() });
      toast.success('Removed');
      await refresh();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const canEdit = ['submitted', 'draft', 'pending_approval'].includes(exp.status);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="my-expense-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {exp.title}
            <StatusPill status={exp.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" value={fmtMoney(exp.amount)} />
            <Field label="Date" value={fmtDate(exp.expense_date)} />
            <Field label="Category" value={exp.category_name} />
            <Field label="Vendor" value={exp.vendor_name || '—'} />
            <Field label="Invoice #" value={exp.invoice_number || '—'} />
            <Field label="Paid From" value={exp.payment_source === 'manager_cash' ? 'Manager cash wallet' : (exp.paid_by === 'employee' ? 'Own money (reimbursement)' : 'Company')} />
            <Field label="Approved By" value={exp.approved_by_name || '—'} />
            {exp.status === 'rejected' && (
              <Field label="Rejection Reason" value={exp.rejection_reason} colSpan />
            )}
            {exp.status === 'settled' && (
              <>
                <Field label="Reimbursed On" value={fmtDate(exp.settled_at)} />
                <Field label="Reference" value={exp.settlement_reference || '—'} />
              </>
            )}
          </div>
          {exp.description && (
            <div>
              <div className="text-xs uppercase text-wt-text-muted mb-1">Notes</div>
              <p className="text-wt-text-secondary">{exp.description}</p>
            </div>
          )}

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase text-wt-text-muted">Receipts</div>
              {canEdit && (
                <label className="text-xs text-wt-gold cursor-pointer flex items-center gap-1" data-testid="my-detail-upload-label">
                  <Upload className="w-3 h-3" /> {uploading ? 'Uploading…' : 'Add'}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                    onChange={e => upload(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
            {(exp.attachments || []).length === 0 ? (
              <p className="text-xs text-wt-text-muted">No receipts attached.</p>
            ) : (
              <ul className="space-y-1">
                {exp.attachments.map(a => (
                  <li key={a.id} className="flex items-center justify-between text-xs bg-wt-bg/40 rounded px-2 py-1" data-testid={`my-attachment-${a.id}`}>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-wt-gold">
                      <Paperclip className="w-3 h-3" /> {a.name}
                      <span className="text-wt-text-muted">· {(a.size / 1024).toFixed(1)} KB</span>
                    </a>
                    {canEdit && (
                      <button onClick={() => removeAttachment(a)} className="text-rose-400 hover:text-rose-300">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Audit Trail */}
          <div>
            <div className="text-xs uppercase text-wt-text-muted mb-2">Activity</div>
            {audit.length === 0 ? (
              <p className="text-xs text-wt-text-muted">No activity yet.</p>
            ) : (
              <AuditTimeline entries={audit} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value, colSpan }) => (
  <div className={colSpan ? 'col-span-2' : ''}>
    <div className="text-xs uppercase text-wt-text-muted">{label}</div>
    <div className="text-wt-text-primary capitalize">{value ?? '—'}</div>
  </div>
);
